import React from 'react';
import { Box, Typography, alpha, useTheme, useMediaQuery, Alert } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { parseBackendDate } from '../../Dashboard/components/utils';
import { GET_CALENDAR_DAY_DATA } from '../queries';

interface CalendarDayCellProps {
  arg: any;
}

const CalendarDayCellInner: React.FC<{ arg: any; data: any }> = ({ arg, data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const dateStr = dayjs(arg.date).format('YYYY-MM-DD');

  const dayData = data?.getDays?.find((d: any) => {
    const dDate = parseBackendDate(d.date);
    return dDate && dDate.format('YYYY-MM-DD') === dateStr;
  });

  const totalDailiesCount = data?.getDailies?.length || 0;
  const completedDailiesCount = dayData?.completedDailies?.length || 0;

  const completedTasks = data?.getTasks?.filter((t: any) => {
    if (t.status !== 'FINISHED' || !t.completedAt) return false;
    const cDate = parseBackendDate(t.completedAt);
    return cDate && cDate.format('YYYY-MM-DD') === dateStr;
  }) || [];

  const hasJournal = dayData?.journalEntries?.some((j: any) => j.content && j.content.trim().length > 0);

  const showGreenDot = completedDailiesCount > 0;
  const showBlueDot = hasJournal;
  const showPurpleDot = completedTasks.length > 0;

  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      gap: 0.5,
      mt: 0.5
    }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: arg.isToday ? 'bold' : 'normal',
          color: arg.isToday ? theme.palette.primary.main : 'inherit',
          fontSize: '0.9rem',
          lineHeight: 1
        }}
      >
        {arg.dayNumberText}
      </Typography>

      {isMobile ? (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', height: 8 }}>
          {showGreenDot && (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
          )}
          {showBlueDot && (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
          )}
          {showPurpleDot && (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'secondary.main' }} />
          )}
        </Box>
      ) : (
        <>
          {totalDailiesCount > 0 && (
            <Box sx={{
              width: '80%',
              height: 6,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}>
              <Box sx={{
                width: `${(completedDailiesCount / totalDailiesCount) * 100}%`,
                height: '100%',
                bgcolor: theme.palette.success.main
              }} />
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            alignItems: 'center'
          }}>
            {hasJournal && (
              <DescriptionIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
            )}

            {completedTasks.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.info.main }}>
                <AssignmentTurnedInIcon sx={{ fontSize: 20 }} />
                {completedTasks.length > 1 && (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', ml: 0.2, fontWeight: 'bold' }}>
                    {completedTasks.length}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

const CalendarDayCell: React.FC<CalendarDayCellProps> = ({ arg }) => {
  const startOfMonth = dayjs(arg.date).startOf('month').subtract(1, 'week').toISOString();
  const endOfMonth = dayjs(arg.date).endOf('month').add(1, 'week').toISOString();

  const { data, loading, error } = useQuery(GET_CALENDAR_DAY_DATA, {
    variables: { dateRange: { start: startOfMonth, end: endOfMonth } },
    fetchPolicy: 'cache-and-network',
  });

  if (loading) return null;
  if (error) return <Alert severity="error" sx={{ fontSize: '0.75rem', p: 0.5 }}>{error.message}</Alert>;

  return <CalendarDayCellInner arg={arg} data={data} />;
};

export default CalendarDayCell;
